import React, { useMemo, useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Select from '@cloudscape-design/components/select';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';
import AuthCard from '../components/AuthCard';
import { getAuthService } from '../services';
import { normalizePhone, validateCorporateEmail, validatePhone } from '../services/validation';
import { COUNTRIES } from '../config/countries';

interface RegisterScreenProps {
    onRequested: (email: string) => void;
    onGoToLogin: () => void;
}

const COUNTRY_OPTIONS = COUNTRIES.map((c) => ({ value: c.code, label: c.name }));

/**
 * Request access as a supplier. Self sign-up is disabled: the user does NOT set
 * a password here. They submit an onboarding request; once an approver approves
 * it, Cognito emails them a temporary password to finish setting up the account.
 */
export default function RegisterScreen({ onRequested, onGoToLogin }: RegisterScreenProps) {
    const [email, setEmail] = useState('');
    const [company, setCompany] = useState('');
    const [country, setCountry] = useState('');
    const [contactName, setContactName] = useState('');
    const [phone, setPhone] = useState('');
    const [teamHint, setTeamHint] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const selectedCountry = useMemo(
        () => COUNTRY_OPTIONS.find((o) => o.value === country) ?? null,
        [country],
    );

    const handleSubmit = async () => {
        setError(null);

        const emailError = validateCorporateEmail(email);
        if (emailError) {
            setError(emailError);
            return;
        }
        if (!company.trim()) {
            setError('Enter your company name.');
            return;
        }
        if (!country) {
            setError('Select your country.');
            return;
        }
        const phoneError = validatePhone(phone);
        if (phoneError) {
            setError(phoneError);
            return;
        }

        setSubmitting(true);
        try {
            await getAuthService().requestOnboarding({
                email: email.trim(),
                company: company.trim(),
                country,
                contactName: contactName.trim() || undefined,
                phone: normalizePhone(phone) || undefined,
                teamHint: teamHint.trim() || undefined,
            });
            onRequested(email.trim());
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not submit your request.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AuthCard title="Request access" subtitle="Ask to be onboarded as a supplier">
            <SpaceBetween size="l">
                <FormField label="Work email">
                    <Input
                        type="email"
                        value={email}
                        autoComplete
                        onChange={({ detail }) => setEmail(detail.value)}
                    />
                </FormField>
                <FormField label="Company name">
                    <Input
                        type="text"
                        value={company}
                        onChange={({ detail }) => setCompany(detail.value)}
                    />
                </FormField>
                <FormField label="Country">
                    <Select
                        placeholder="Select your country"
                        selectedOption={selectedCountry}
                        options={COUNTRY_OPTIONS}
                        filteringType="auto"
                        onChange={({ detail }) => setCountry(detail.selectedOption?.value ?? '')}
                    />
                </FormField>
                <FormField label="Contact name (optional)">
                    <Input
                        type="text"
                        value={contactName}
                        onChange={({ detail }) => setContactName(detail.value)}
                    />
                </FormField>
                <FormField
                    label="Phone number (optional)"
                    description="Include country code, e.g. +1 415 555 0123."
                >
                    <Input
                        type="text"
                        inputMode="tel"
                        value={phone}
                        onChange={({ detail }) => setPhone(detail.value)}
                    />
                </FormField>
                <FormField
                    label="Team or brand (optional)"
                    description="If your company already uses Supplier Gateway, tell us which team to join."
                >
                    <Input
                        type="text"
                        value={teamHint}
                        onChange={({ detail }) => setTeamHint(detail.value)}
                    />
                </FormField>
                {error ? <Alert type="error">{error}</Alert> : null}
                <Button variant="primary" loading={submitting} onClick={handleSubmit}>
                    Request access
                </Button>
                <Button variant="link" onClick={onGoToLogin}>
                    Already have an account? Sign in
                </Button>
            </SpaceBetween>
        </AuthCard>
    );
}
